import { CareEnvironment, CarePhase } from '../../../../appModel';
import {
  calcCareMattersDeathBenefit,
  calcRiderMonthly,
} from '../../calcCareMattersCoverage';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  getBenefitPeriodMonths,
  getCalendarPolicyYear,
  getDailyBenefitAmountForEnvironment,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  getPhasePolicyStartYear,
  getRiderForm,
  getWaitingPeriodBucketForEnvironment,
} from '../sharedPolicyConfig';
import { applyIndemnitySurplus } from '../sharedSurplus';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import { deriveIneligibilityReason } from '../deriveIneligibilityReason';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';

// ---------------------------------------------------------------------------
// CM II strategy state + deltas.
//
// `strategyState` carries the per-claim/per-month state that's specific to
// CareMatters II — the indemnity-surplus reserve and the phase-scoped paid-
// months counter (which walks the rider's policy-year clock). `strategyDeltas`
// carries the per-month flows that are also CM II–specific: the base/rider
// split of the monthly payout and the EP retroactive lump line items.
// ---------------------------------------------------------------------------

export interface CmTwoState {
  indemnitySurplus: number;
  // Phase-scoped counter — count of paying months in the current phase.
  // Resets to 0 at monthsIntoPhase === 0. Used by CareMatters II to walk
  // the rider's policy-year clock by paid months (matches legacy
  // `simulateCareMattersPhase` semantics — the rider does not advance on
  // non-paying months).
  phasePaidMonthsSoFar: number;
  // Whether the retroactive EP reimbursement lump has already fired. The
  // elimination period is reimbursed ONCE per claim (matching legacy's single
  // `epReimbursed` flag) — not once per care-environment WP bucket. Without
  // this, an asymmetric policy (e.g. home 60 / facility 90) would pay the lump
  // a second time when the facility bucket later satisfies.
  epReimbursed: boolean;
}

export interface CmTwoDeltas {
  baseBenefitPaid: number;
  riderBenefitPaid: number;
  baseBenefitDays: number;
  riderBenefitDays: number;
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
  // Total dollars paid out by the EP retroactive WP lump this month (base
  // portion + rider portion). Used to compute cumulative payout and the
  // "WP reimbursement" UI line.
  epLumpSumPaid: number;
  // Base-pool-depleting portion of the EP lump (days × baseDaily). Only
  // this portion reduces the base pool / deathBenefit — the rider portion
  // is paid on top, mirroring the monthly base+rider model.
  epLumpSumBasePaid: number;
  epLumpSumDays: number;
  epLumpSumDailyRate: number;
}

// CM II's retroactive WP lump. Fires on the first month where home OR
// facility WP transitions from positive → 0. The lump pays
// `daysServed × (baseDaily + riderDaily)` total; only the base portion
// (`daysServed × baseDaily`) depletes the base pool, matching CM II's
// regular monthly pay model where the rider sits on top of the pool.
// Cumulative days-served is reconstructed from initial − current on
// whichever pool started larger (both decrement equally per month, so the
// larger pool is the canonical counter; the smaller hits 0 first and stops
// contributing). When the pool can't cover the full served-days at base
// daily, the lump scales proportionally — fewer days are funded and both
// base and rider stop together for the unfunded tail.
interface EpLumpInputs {
  ltcPolicy: PolicyFundingSource;
  careEnvironment: CareEnvironment;
  openState: MonthlyState<CmTwoState>;
  closeHomeWp: number;
  closeFacilityWp: number;
  baseBenefitPoolAfterBase: number;
  baseMonthly: number;
  // Rider monthly at the policy year of WP satisfaction. Year 1 is $0 (the
  // rider only steps up after the first anniversary — see `calcRiderMonthly`).
  riderMonthly: number;
  // Whether the lump has already fired earlier in the claim. The EP is
  // reimbursed once per claim, so a second bucket satisfying later must not
  // fire again.
  alreadyReimbursed: boolean;
}

interface EpLumpResult {
  epLumpSumPaid: number;
  epLumpSumBasePaid: number;
  epLumpSumDays: number;
  epLumpSumDailyRate: number;
  // True on the row where the lump fires (the EP's first satisfaction while
  // retroactive reimbursement is enabled). Flips the per-claim `epReimbursed`
  // latch so it never fires again.
  fired: boolean;
}

function computeEpRetroactiveLump(input: EpLumpInputs): EpLumpResult {
  const {
    ltcPolicy,
    careEnvironment,
    openState,
    closeHomeWp,
    closeFacilityWp,
    baseBenefitPoolAfterBase,
    baseMonthly,
    riderMonthly,
    alreadyReimbursed,
  } = input;

  // Fire when the active environment's elimination-period bucket is fully
  // satisfied for this month — but only once per claim. The EP reimbursement
  // is a single policy-level event: whichever environment's WP satisfies first
  // triggers it, and a later bucket satisfaction must not fire it again.
  const wpBucket = getWaitingPeriodBucketForEnvironment(careEnvironment);
  const openRelevantWp =
    wpBucket === 'homeDays'
      ? openState.waitingPeriod.homeDays
      : openState.waitingPeriod.facilityDays;
  const closeRelevantWp = wpBucket === 'homeDays' ? closeHomeWp : closeFacilityWp;
  const epJustSatisfied = openRelevantWp > 0 && closeRelevantWp === 0;
  const epEligible =
    ltcPolicy.hasRetroactiveWaitingPeriodReimbursement === true &&
    epJustSatisfied &&
    !alreadyReimbursed;

  if (!epEligible) {
    return {
      epLumpSumPaid: 0,
      epLumpSumBasePaid: 0,
      epLumpSumDays: 0,
      epLumpSumDailyRate: 0,
      fired: false,
    };
  }

  // Mirror the same Remaining ?? Days ?? 0 fallback chain that initClaimState
  // uses to seed the WP balance. For a not-yet-on-claim policy `…Remaining`
  // is null but `…Days` carries the configured WP — without the fallback
  // here, cumulativeWpDaysServed evaluates to 0 and the lump silently zeros.
  const initialHomeWp =
    ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
    ltcPolicy.homeCareWaitingPeriodDays ??
    0;
  const initialFacilityWp =
    ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
    ltcPolicy.facilityCareWaitingPeriodDays ??
    0;
  const initialRelevantWp = wpBucket === 'homeDays' ? initialHomeWp : initialFacilityWp;
  const cumulativeWpDaysServed = Math.max(initialRelevantWp - closeRelevantWp, 0);

  const baseDaily = baseMonthly / DAYS_IN_MONTH;
  const riderDaily = riderMonthly / DAYS_IN_MONTH;
  const inflatedDaily = baseDaily + riderDaily;

  // Cap by base pool: pool can fund at most `pool / baseDaily` days at the
  // base rate. Rider scales together with base — both stop on the same day.
  const maxDaysFundable = baseDaily > 0 ? baseBenefitPoolAfterBase / baseDaily : 0;
  const daysFunded = Math.min(cumulativeWpDaysServed, Math.max(maxDaysFundable, 0));
  const epLumpSumBasePaid = daysFunded * baseDaily;
  const epLumpSumPaid = daysFunded * inflatedDaily;

  return {
    epLumpSumPaid,
    epLumpSumBasePaid,
    epLumpSumDays: daysFunded,
    epLumpSumDailyRate: inflatedDaily,
    fired: true,
  };
}

// CM-II specific: rider walks paid months (not calendar months). Other
// products that key their rider off calendar time should NOT use this
// helper — it's intentionally local to the CM strategy.
function policyYearForPayingRow(
  phasePolicyStartYear: number,
  paidMonthsSoFarInPhase: number,
): number {
  const phaseStartYearFloor = Math.floor(phasePolicyStartYear);
  // Match the simulator's epsilon-floor to avoid an early year-bump from
  // floating-point fuzz at near-anniversary fractional inputs.
  const monthsIntoStartYear = Math.floor(
    (phasePolicyStartYear - phaseStartYearFloor) * 12 + 1e-6,
  );
  const monthsFromYearStart = monthsIntoStartYear + paidMonthsSoFarInPhase;
  return phaseStartYearFloor + Math.floor(monthsFromYearStart / 12);
}

export const cmTwoStrategy: PolicyProjectionStrategy<CmTwoState, CmTwoDeltas> = {
  initClaimState(_client, ltcPolicy): MonthlyState<CmTwoState> {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const benefitPeriodMonths = getBenefitPeriodMonths(ltcPolicy);
    const totalBasePool = baseMonthly * benefitPeriodMonths;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const basePool = Math.max(totalBasePool - utilizedToDate, 0);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;

    return {
      pools: {
        baseBenefitPool: basePool,
        extensionOfBenefitsPool: 0,
        deathBenefit: Math.max(faceAmount - utilizedToDate, minGuaranteed),
      },
      waitingPeriod: {
        // `…WaitingPeriodDaysRemaining` is the on-claim runtime counter — set
        // only after the client has begun a claim. For a projection of a
        // not-yet-on-claim policy it is null, in which case we must fall back
        // to the configured WP days. Without this fallback the projection
        // would start with WP = 0 and the policy would pay from month 1
        // instead of serving the elimination period first.
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
          ltcPolicy.homeCareWaitingPeriodDays ??
          0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: {
        indemnitySurplus: 0,
        phasePaidMonthsSoFar: 0,
        epReimbursed: false,
      },
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const riderForm = getRiderForm(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = clientAsClient.clientAddedDateTime
      ? clientAsClient.clientAddedDateTime.getFullYear()
      : new Date().getFullYear();
    return {
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math
      // uses (getMonthlyBenefitAmountForEnvironment → getDailyBenefitAmountForEnvironment).
      // A raw `?? 0` here would turn a sparse/unpopulated env field into a hard
      // $0 that the presentation's `?? nominalDailyBenefitBase` can't rescue (0
      // isn't nullish), so the table would show a $0 cap while the math paid at
      // the resolved max. The helper honors an explicit 0 ("not covered") and
      // falls back to the max populated daily for sparse imports.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(ltcPolicy, CareEnvironment.home),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      inflationRate: riderForm.rate,
      inflationCapYears: riderForm.capYears ?? 0,
      inflationIsSimple: riderForm.simple,
      // baseBenefitPool / extensionOfBenefitsPool inflation knobs are
      // intentionally omitted — CM II's base pool is flat (no per-pool
      // inflation concept; rider escalation lives on `inflationRate`
      // above), and CM II has no EOB pool.
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: false,
      isIndemnity: true,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear, calendarYearOffset } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const riderForm = getRiderForm(ltcPolicy);
    const riderMonthly = calcRiderMonthly(baseMonthly, policyYear, riderForm);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0; // CM II flat
    const totalMonthlyBenefit = baseMonthly + riderMonthly;
    // CM II's base benefit pool is flat — it does not inflate (the rider
    // grows the monthly benefit instead). Its theoretical lifetime ceiling is
    // the constant base-monthly × benefit-period months.
    const benefitPeriodMonths = getBenefitPeriodMonths(ltcPolicy);
    return {
      policyYear,
      calendarYearOffset,
      effectiveDailyBenefit: dailyBenefit + riderMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: dailyBenefit,
      effectiveDailyBenefitRider: riderMonthly / DAYS_IN_MONTH,
      effectiveFaceAmount: faceAmount,
      effectiveContinuationBenefit: 0,
      effectiveTotalLtcBenefit: totalMonthlyBenefit,
      monthlyBenefitCap: totalMonthlyBenefit,
      theoreticalLtcPoolCeiling: baseMonthly * benefitPeriodMonths,
    };
  },

  computeMonth(
    input: MonthComputeInput<CmTwoState, CmTwoDeltas>,
  ): MonthlyEntry<CmTwoState, CmTwoDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    // 1. Day classification:
    //    a. 2-ADL bucket (first daysInPhase * 0.5 days, when applicable)
    //    b. Waiting period (consume from pool matching phase's care env)
    //    c. Remaining days are eligible
    const non2AdlDaysThisRow = computeNon2AdlDaysThisRow(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDaysThisRow;

    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;

    const eligibleDaysThisRow = daysAfter2Adl - wpDaysThisRow;

    // 2. Paid-month counter (phase-scoped).
    const paidSoFar =
      monthsIntoPhase === 0 ? 0 : openState.strategyState.phasePaidMonthsSoFar;

    // 3. Per-row dollar math.
    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const riderForm = getRiderForm(ltcPolicy);
    const phasePolicyStartYear = getPhasePolicyStartYear(clientAsClient, phaseCarePlan);

    // CM II rule: only full-month rows pay, and only when the base pool can
    // cover a full month's benefit. Partial final months strand the sub-
    // monthly remainder in the pool (matches legacy `floor(pool/baseMonthly)`
    // — carriers don't cut a partial indemnity check).
    const canAffordFullMonth = openState.pools.baseBenefitPool + 1e-6 >= baseMonthly;
    const isPayingRow = eligibleDaysThisRow === DAYS_IN_MONTH && canAffordFullMonth;

    let basePay = 0;
    let riderPay = 0;
    let policyYearForRow = phasePolicyStartYear;
    if (isPayingRow) {
      policyYearForRow = policyYearForPayingRow(phasePolicyStartYear, paidSoFar);
      basePay = baseMonthly;
      riderPay = calcRiderMonthly(baseMonthly, policyYearForRow, riderForm);
    } else {
      // Non-paying rows: use the calendar-anchored policy year, so the
      // display year advances every 12 monthIndex rows (instead of being a
      // per-phase constant that can collapse two phases sharing a predicted
      // start year into one year). Rider math on paying rows still uses
      // the paid-month-walking formula — see `policyYearForPayingRow`.
      policyYearForRow = getCalendarPolicyYear(clientAsClient, monthIndex);
    }
    const indemnityPayout = basePay + riderPay;

    // 4. Per-month effective care cost (annual-stepped from today, anchored
    //    at the phase's predicted start year).
    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const monthlyBenefitEligibleCost = costMetrics.dailyCareCost * eligibleDaysThisRow;

    // 5. Indemnity-vs-cost surplus accounting (per-row).
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout,
      eligibleCost: monthlyBenefitEligibleCost,
      fullCost: costMetrics.effectiveCareCost,
    });

    // 6. Pool depletion.
    const baseBenefitPoolAfterBase = Math.max(openState.pools.baseBenefitPool - basePay, 0);

    // 7. EP retroactive lump. The reimbursement applies the rider step at
    //    the policy year of WP satisfaction — total paid = days × (base +
    //    rider) daily, but only the base portion depletes the base pool.
    const lumpRiderMonthly = calcRiderMonthly(baseMonthly, policyYearForRow, riderForm);
    const epResult = computeEpRetroactiveLump({
      ltcPolicy,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      openState,
      closeHomeWp,
      closeFacilityWp,
      baseBenefitPoolAfterBase,
      baseMonthly,
      riderMonthly: lumpRiderMonthly,
      alreadyReimbursed: openState.strategyState.epReimbursed,
    });

    // 8. Closing balances. Only the base portion of the lump depletes the
    //    pool / death benefit; the rider portion is paid on top (matches CM
    //    II's regular monthly base+rider split where rider does not draw
    //    from the base pool).
    const closeBaseBenefitPool = Math.max(
      baseBenefitPoolAfterBase - epResult.epLumpSumBasePaid,
      0,
    );
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const dbRawDrawdown = basePay + epResult.epLumpSumBasePaid;
    const closeDeathBenefit = Math.max(
      openState.pools.deathBenefit - dbRawDrawdown,
      minGuaranteed,
    );
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && dbRawDrawdown > deathBenefitReduction;

    const context: MonthContext = {
      monthIndex,
      policyYear: policyYearForRow,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYearForRow),
      calendarYearOffset: Math.floor(monthIndex / 12),
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      non2AdlDays: non2AdlDaysThisRow,
      coverageEligibleDays: eligibleDaysThisRow,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
    };

    const ineligibleCost =
      costMetrics.effectiveCareCost -
      surplusResult.ltcPayoutToCost -
      epResult.epLumpSumPaid;

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      ineligibleCost,
      indemnityPayout,
    };

    const deltas: MonthlyDeltas<CmTwoDeltas> = {
      payout,
      // CM II's base pool drains by the regular monthly base benefit plus
      // the base portion of any EP retroactive lump on the same row. The
      // rider portion is paid on top — it does not deplete the pool.
      base: {
        depletion: basePay + epResult.epLumpSumBasePaid,
        inflationAccrual: 0,
      },
      eob: { depletion: 0, inflationAccrual: 0 },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {
        baseBenefitPaid: basePay,
        riderBenefitPaid: riderPay,
        baseBenefitDays: isPayingRow ? DAYS_IN_MONTH : 0,
        riderBenefitDays: isPayingRow ? DAYS_IN_MONTH : 0,
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
        epLumpSumPaid: epResult.epLumpSumPaid,
        epLumpSumBasePaid: epResult.epLumpSumBasePaid,
        epLumpSumDays: epResult.epLumpSumDays,
        epLumpSumDailyRate: epResult.epLumpSumDailyRate,
      },
    };

    const closeState: MonthlyState<CmTwoState> = {
      pools: {
        baseBenefitPool: closeBaseBenefitPool,
        extensionOfBenefitsPool: 0,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: {
        homeDays: closeHomeWp,
        facilityDays: closeFacilityWp,
      },
      strategyState: {
        indemnitySurplus: surplusResult.closeSurplus,
        phasePaidMonthsSoFar: paidSoFar + (isPayingRow ? 1 : 0),
        epReimbursed: openState.strategyState.epReimbursed || epResult.fired,
      },
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBaseBenefitPool <= 0,
      clientPoolExhausted: closeBaseBenefitPool <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<CmTwoState, CmTwoDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: epResult.epLumpSumPaid,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: riderForm.rate > 0,
    });

    const ineligibilityReason = deriveIneligibilityReason<CmTwoState>({
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    });

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason },
    };
  },

  finalizeDeathBenefit({
    ltcPolicy,
    closingState,
  }: FinalizeDeathBenefitInput<CmTwoState>): number {
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const baseDollarsPaid = Math.max(faceAmount - closingState.pools.deathBenefit, 0);
    return calcCareMattersDeathBenefit({
      specifiedAmount: faceAmount,
      baseDollarsPaid,
      storedFloorAmount: ltcPolicy.policyMinimumGuaranteedBenefitAmount,
    });
  },
};
